// MovementHistory — the back face of a movement module (flipped to via the
// header clock icon; see <PlacedModule>). One chart, one stepped line per past
// match: how this market's price moved over the 7 days before kick-off in each
// of the selected team's last 10 matches. The opponent picker highlights one
// line; the view buttons re-express the same series (odds / Asian Handicap /
// change in points / % change).
//
// Data arrives with the module's own payload (`history`, from app-api via
// useWidgetData — React Query shares the front face's fetch). The bookmaker,
// line (Match Goals/Cards) and player (Player Goals/Cards) are the front face's
// — linked, not second controls; the line/player shows as a label. Team /
// opponent / view are local UI state: transient, not part of a saved board.

import { useEffect, useRef, useState, type ReactElement } from "react";
import { select } from "d3-selection";
import { scaleLinear, scalePoint } from "d3-scale";
import { line, curveStepAfter } from "d3-shape";
import { clubFromName, shapeHistoryLines, HISTORY_VIEWS, type HistoryLine, type HistoryMatch, type HistoryViewKey, type LineHistoryData, type MovementHistoryData, type MovementPoint } from "@bento/shared";
import { useWidgetData } from "../data/useWidgetData.js";
import { useFixtureKey } from "../data/useFixtureKey.js";
import { selectedPlayerFor } from "../data/playerControlOptions.js";
import type { ModuleControls } from "../store/module.js";
import BentoSelect from "./BentoSelect.js";
import { MOVEMENT_HISTORY } from "./movementHistoryConfig.js";

const VIEW_ICONS: Record<HistoryViewKey, ReactElement> = {
  odds: <polyline points="3 17 9 11 13 15 21 6" />,
  ah: (
    <>
      <line x1="7" y1="4" x2="7" y2="10" /><line x1="4" y1="7" x2="10" y2="7" />
      <line x1="19" y1="5" x2="5" y2="19" /><line x1="14" y1="17" x2="20" y2="17" />
    </>
  ),
  rebased: (
    <>
      <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <line x1="4" y1="12" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="18" />
    </>
  ),
  pct: (
    <>
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" />
    </>
  ),
};

function draw(chartEl: HTMLElement, lines: HistoryLine[], active: string, view: HistoryViewKey): void {
  const w = chartEl.clientWidth;
  const h = chartEl.clientHeight;
  select(chartEl).select("svg").remove();
  if (w < 20 || h < 20 || !lines.length) return; // the ResizeObserver retries

  const ML = 44, MR = 16, MT = 8, MB = 22;
  const cW = w - ML - MR;
  const cH = h - MT - MB;
  const fmt = HISTORY_VIEWS[view].fmt;

  const all = lines.flatMap((l) => l.pts.map((p) => p.o));
  const minO = Math.min(...all);
  const maxO = Math.max(...all);
  const pad = (maxO - minO) * 0.08 || 0.2;
  const axis = lines[0]!.pts;

  const xSc = scalePoint().domain(axis.map((d) => d.t)).range([0, cW]).padding(0.1);
  const ySc = scaleLinear().domain([minO - pad, maxO + pad]).range([cH, 0]);

  const svg = select(chartEl).append("svg").attr("width", w).attr("height", h);
  const g = svg.append("g").attr("transform", `translate(${ML},${MT})`);

  ySc.ticks(4).forEach((tk) => {
    g.append("line")
      .attr("x1", 0).attr("x2", cW).attr("y1", ySc(tk)).attr("y2", ySc(tk))
      .attr("stroke", "rgba(255,255,255,0.06)").attr("stroke-width", 1);
    g.append("text")
      .attr("x", -6).attr("y", ySc(tk)).attr("dy", "0.32em").attr("text-anchor", "end")
      .attr("fill", "#999").style("font-size", "var(--t-label)")
      .text(fmt(tk));
  });

  // X-axis labels — thinned to the available width (13 points crowd otherwise).
  const maxLabels = Math.max(2, Math.floor(cW / 28));
  const step = Math.ceil(axis.length / maxLabels);
  axis.forEach((d, i) => {
    if (i % step !== 0 && i !== axis.length - 1) return;
    g.append("text")
      .attr("x", xSc(d.t)!).attr("y", cH + 14).attr("text-anchor", "middle")
      .attr("fill", "#999").style("font-size", "var(--t-label)")
      .text(d.t);
  });

  const lineFn = line<MovementPoint>()
    .x((d) => xSc(d.t)!)
    .y((d) => ySc(d.o))
    .curve(curveStepAfter);

  // Paint order = z-order in SVG, so the highlighted line is drawn last (on top).
  const ordered = [...lines.filter((l) => l.opponent !== active), ...lines.filter((l) => l.opponent === active)];
  const linesG = g.append("g").attr("class", "mh-lines");
  ordered.forEach((l) => {
    linesG.append("path")
      .attr("class", "mh-line" + (l.opponent === active ? " mh-line-active" : ""))
      .attr("data-opponent", l.opponent)
      .attr("d", lineFn(l.pts) ?? "");
  });
}

export interface MovementHistoryProps {
  title: string;
  fixture: string;
  /** The front face's control values (bookmaker, and line / player / scope). */
  controls: Partial<ModuleControls> | undefined;
}

export default function MovementHistory({ title, fixture, controls }: MovementHistoryProps) {
  const cfg = MOVEMENT_HISTORY[title];
  const fix = fixture || "Arsenal v Everton";
  const [home = "Home", away = "Away"] = fix.split(" v ");
  const [team, setTeam] = useState(home);
  const [opponent, setOpponent] = useState("");
  const [view, setView] = useState<HistoryViewKey>("odds");
  const chartRef = useRef<HTMLDivElement>(null);

  const { data } = useWidgetData(cfg?.widgetKey ?? "winner-1x2-movement", useFixtureKey(fix));
  const payload = data as
    | { history?: unknown; bkOffsets?: Record<string, number>; labels?: Record<string, string> }
    | undefined;

  // A fixture change can leave the picked team/opponent stale — fall back.
  const teamVal = team === home || team === away ? team : home;
  // Match Goals/Cards follow the front scope: "Match" = match totals (pick the
  // team here); a team scope = that team's own lines (no team pick needed).
  const scope = controls?.scope || "Match";
  const teamScoped = cfg?.kind === "teamLine" && scope !== "Match";
  let matches: HistoryMatch[] = [];
  let label = "";
  if (cfg?.kind === "team") {
    const h = (payload?.history ?? {}) as MovementHistoryData;
    matches = h[teamVal] ?? h["Arsenal"] ?? []; // dummy data: unknown teams borrow Arsenal's
  } else if (cfg?.kind === "teamLine") {
    const h = (payload?.history ?? {}) as LineHistoryData;
    const line = controls?.marketLine || cfg.defaultLine || "";
    const lineLabel = payload?.labels?.[line] ?? line;
    if (teamScoped) {
      // Front scope is a team: that team's own line. The scope values are the
      // home/away slot keys ("ARS" = home, "EVE" = away); dummy data borrows
      // Arsenal's / Everton's history for other fixtures.
      const scopedTeam = scope === "ARS" ? home : away;
      matches = (h[scopedTeam] ?? h[scope === "ARS" ? "Arsenal" : "Everton"])?.Team?.[line] ?? [];
      label = `${clubFromName(scopedTeam)?.short ?? scopedTeam.slice(0, 3).toUpperCase()} ${lineLabel}`;
    } else {
      matches = (h[teamVal] ?? h["Arsenal"])?.Match?.[line] ?? [];
      label = lineLabel;
    }
  } else if (cfg?.kind === "player") {
    const h = (payload?.history ?? {}) as MovementHistoryData;
    const player = selectedPlayerFor(
      cfg.playerKind ?? "goals",
      fix,
      controls?.scope,
      controls?.marketType,
      controls?.player
    );
    matches = h[player] ?? [];
    label = player ? `${player} — Anytime` : ""; // history is Anytime prices only
  }
  const oppVal = matches.some((m) => m.opponent === opponent) ? opponent : (matches[0]?.opponent ?? "");
  const offset = (payload?.bkOffsets ?? {})[controls?.bookmaker || "Pinnacle"] ?? 0;
  const lines = shapeHistoryLines(matches, offset, view);

  const sig = JSON.stringify([lines, oppVal, view]);
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    draw(el, lines, oppVal, view);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => draw(el, lines, oppVal, view));
    ro.observe(el);
    return () => ro.disconnect();
    // `sig` captures every draw input; the closure is fresh on each run.
  }, [sig]);

  if (!cfg) return null;

  return (
    <>
      <div className="module-sub-controls history-sub-controls">
        {cfg.kind !== "player" && !teamScoped && (
          <BentoSelect
            className="history-team-toggle"
            ariaLabel="Team"
            value={teamVal}
            options={[home, away].map((t) => ({ value: t, label: t }))}
            onChange={setTeam}
          />
        )}
        <BentoSelect
          className="history-opponent-toggle"
          ariaLabel="Opponent"
          value={oppVal}
          options={matches.map((m) => ({ value: m.opponent, label: "v " + m.opponent }))}
          onChange={setOpponent}
        />
      </div>
      <div className="history-view-row">
        <div className="history-view-btns" role="group" aria-label="Chart view">
          {cfg.views.map((v) => (
            <button
              key={v}
              type="button"
              className={"history-view-btn" + (v === view ? " active" : "")}
              title={HISTORY_VIEWS[v].label}
              onClick={() => setView(v)}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {VIEW_ICONS[v]}
              </svg>
            </button>
          ))}
        </div>
        {label && <div className="history-view-label">{label}</div>}
      </div>
      <div className="module-body history-body">
        {matches.length ? (
          <div className="mh-wrapper bm-wrapper">
            <div className="mh-chart" ref={chartRef} />
          </div>
        ) : (
          <div className="mh-empty">No movement history yet.</div>
        )}
      </div>
    </>
  );
}
